import { useCallback, useEffect, useRef, useState } from 'react';
import { startApiCallPage } from 'src/components/star-grid/services/apiCallLog';
import { NavigationReport } from '../navigation.types';
import { StarGridView } from '../starGridConstants';
import { canonicalStarGridHash, reportHrefById, resolveStarGridHash } from '../utils/starGridNavigation';

export interface UseStarGridNavResult {
  activeView: StarGridView;
  activeReportId: number | undefined;
  activeHref: string;
  setActiveView: (view: StarGridView) => void;
  openReport: (reportId: number) => void;
  navigationOpen: boolean;
  setNavigationOpen: (open: boolean) => void;
}

const EMPTY_REPORTS: readonly NavigationReport[] = [];

function useHashNavigation() {
  const [hash, setHash] = useState(() => window.location.hash);
  // The page `navigate` already opened: its own hashchange arrives after the new page's first calls
  // went out, and starting the page again there dropped them from Show stats.
  const navigated = useRef<string>();
  useEffect(() => {
    const onHashChange = () => {
      if (window.location.hash !== navigated.current) startApiCallPage();
      navigated.current = undefined;
      setHash(window.location.hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const navigate = useCallback((href: string) => {
    startApiCallPage();
    navigated.current = href;
    setHash(href);
    if (window.location.hash !== href) window.location.hash = href;
  }, []);
  return { hash, setHash, navigate };
}

function useCanonicalHash(hash: string, reports: readonly NavigationReport[], setHash: (hash: string) => void): string {
  const canonicalHash = canonicalStarGridHash(hash, reports);
  useEffect(() => {
    if (hash === canonicalHash || window.location.hash !== hash) return;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${canonicalHash}`);
    setHash(canonicalHash);
  }, [hash, canonicalHash, setHash]);
  return canonicalHash;
}

/** Resolve name links after the catalog loads; keep numeric-id bookmarks and browser history working. */
export function useStarGridNav(reports: readonly NavigationReport[] = EMPTY_REPORTS): UseStarGridNavResult {
  const { hash, setHash, navigate } = useHashNavigation();
  const activeHref = useCanonicalHash(hash, reports, setHash);
  const { view: activeView, reportId: activeReportId } = resolveStarGridHash(hash, reports);
  const [navigationOpen, setNavigationOpen] = useState(true);
  const setActiveView = useCallback((view: StarGridView) => navigate(`#${view}`), [navigate]);
  const openReport = useCallback((reportId: number) => navigate(reportHrefById(reportId, reports)), [navigate, reports]);
  return { activeView, activeReportId, activeHref, setActiveView, openReport, navigationOpen, setNavigationOpen };
}
